
import { Button } from "@/components/ui/button";
import { Mail } from "lucide-react";
import NotificationToggle from "./NotificationToggle";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";

interface EmailNotificationProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled: boolean;
  userEmail: string | null;
}

const EmailNotification = ({ 
  checked, 
  onCheckedChange, 
  disabled, 
  userEmail 
}: EmailNotificationProps) => {
  const { toast } = useToast();
  const [isSending, setIsSending] = useState(false);

  const handleSendTestEmail = async () => {
    if (!userEmail) {
      toast({
        title: "Error",
        description: "No email address found for your account",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    setIsSending(true);
    
    toast({
      title: "Sending Test Email",
      description: "Please wait...",
      duration: 2000,
    });

    try {
      const { error } = await supabase.functions.invoke('send-email', {
        body: { 
          to: userEmail,
          subject: "SupplyMate Test Email",
          html: `
            <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #3b82f6; margin-bottom: 20px;">SupplyMate Test Email</h2>
              <p>Hello,</p>
              <p>This is a test email from SupplyMate to confirm your email notifications are working correctly.</p>
              <div style="background-color: #f3f4f6; border-left: 4px solid #3b82f6; padding: 15px; margin: 20px 0;">
                <p style="margin: 0;">Your notification settings have been saved successfully.</p>
              </div>
              <p>Thank you for using SupplyMate!</p>
            </div>
          `,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: "Test Email Sent",
        description: `A test email has been sent to ${userEmail}`,
        duration: 5000,
      });
    } catch (error) {
      console.error('Error sending test email:', error);
      toast({
        title: "Failed to Send Test Email",
        description: "Please check your connection and try again later",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-4">
      <NotificationToggle
        id="email-notifications"
        icon={<Mail className="h-5 w-5 text-blue-500" />}
        title="Email Notifications"
        description={userEmail ? `Alerts will be sent to: ${userEmail}` : undefined}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
      />
      
      {checked && !disabled && (
        <div className="ml-7 mt-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleSendTestEmail}
            disabled={!userEmail || isSending}
          >
            {isSending ? "Sending..." : "Send Test Email"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default EmailNotification;
